/** @jsx jsx */
/** @jsxFrag Fragment */
import IconAdd from '@apollo/icons/default/IconAdd.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import { Button } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import * as monacoEditor from 'monaco-editor';
import { Fragment } from 'react';

import { preflightOAuth2RedirectRoute } from 'src/app/shared/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { Config } from 'src/lib/config/config';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';

import { GraphRef, graphRefToString, useGraphRef } from '../hooks/useGraphRef';
function cursor(text: string) {
  return `${cursorStart}${text}${cursorEnd}`;
}
const cursorStart = `<cursor>`;
const cursorEnd = `</cursor>`;
const cursorRegex = /<cursor>[^<]*<\/cursor>/;

function placeholderText(text: string) {
  return `${placeholderStart}${text}${placeholderEnd}`;
}
const placeholderStart = `<placeholder>`;
const placeholderEnd = `</placeholder>`;
const placeholderRegex = /<placeholder>[^<]*<\/placeholder>/g;

const getModalSnippets = (graphRef: GraphRef | null) => ({
  'Operation Response': [
    {
      label: 'Get operation response',
      info: 'explorer.response.body will only be populated when running a postflight script.',
      snippet: 'const response = explorer.response.body',
    },
  ],
  Requests: [
    {
      label: 'Run an operation',
      snippet: `const response = await explorer.runOperation({
  scope: '${graphRef ? 'shared' : 'sandbox'}',
  graphRef: ${graphRef ? `'${graphRefToString(graphRef)}'` : 'null'},
  collectionName: '${cursor('collectionName')}',
  operationName: '${placeholderText('operationName')}',
  headers: {},
  variables: {}
})`,
    },
    {
      label: 'Send request',
      // TODO: what URL do we want here?
      snippet: `const response = await explorer.fetch('${cursor(
        'https://YOUR_URL',
      )}', {
  method: 'POST',
  headers: {},
  body: JSON.stringify({}),
})`,
    },
  ],
  'Environment variables': [
    {
      label: 'Get variable',
      snippet: `explorer.environment.get('${cursor('key')}')`,
    },
    {
      label: 'Set variable',
      snippet: `explorer.environment.set('${cursor('key')}', 'value')`,
    },
  ],
  'User input': [
    {
      label: 'Request user input',
      snippet: `const userResponse = await explorer.prompt('${cursor(
        'Question?',
      )}')`,
    },
    {
      label: 'Request OAuth 2.0 login.',
      info: `Make sure to safelist ${
        new URL(preflightOAuth2RedirectRoute.path({}), Config.absoluteUrl).href
      } as a redirect URL when setting up this client with your authentication provider. This is where we redirect to pass back your auth token.`,
      snippet: `const responseQueryParams = await explorer.oauth2Request(
  '${cursor('https://yourauthprovider.com/oauth2/authorize')}',
  {  
    client_id: "${placeholderText('xyz123')}",
    state: "${placeholderText('abc')}",
    scope: "openid",
    response_type: "code",
    code_challenge_method: "S256",
    code_challenge: "${placeholderText('abc')}",
    code_verifier: "${placeholderText('xyc')}",
  })`,
    },
  ],
  CryptoJS: [
    {
      label: 'Calculate request body hash',
      snippet: `const requestBodyHash = explorer.CryptoJS.SHA256(explorer.request.body)`,
    },
  ],
});

export function ScriptModalSnippets({
  scriptEditor,
  model,
  isPostflight,
}: {
  scriptEditor: monacoEditor.editor.IStandaloneCodeEditor;
  model: monacoEditor.editor.ITextModel | null;
  isPostflight?: boolean;
}) {
  const shouldShowPostflightScripts = useLDFlag('nebula-postflight-scripts');
  const graphRef = useGraphRef();
  return (
    <>
      {Object.entries(getModalSnippets(graphRef)).map(([section, snippets]) => {
        if (
          (!shouldShowPostflightScripts || !isPostflight) &&
          section === 'Operation Response'
        ) {
          return;
        }
        return (
          <div key={section} className="ml-8 mr-6">
            {snippets.map((snippet) => {
              return (
                <div key={snippet.label} className="mt-2">
                  <div className="mb-px mt-4 flex items-center">
                    <Button
                      variant="hidden"
                      onClick={() => {
                        const position =
                          scriptEditor.getPosition() ??
                          new monacoEditor.Position(0, 0);

                        const whitespace =
                          model
                            ?.getValue()
                            .slice(0, model.getOffsetAt(position))
                            .match(/($|\n)([\t ]*)$/)?.[2] ?? '';
                        const cursorIndex =
                          snippet.snippet.indexOf(cursorStart);
                        scriptEditor.executeEdits(
                          'add_snippet',
                          [
                            {
                              text: snippet.snippet
                                .replace(cursorRegex, '')
                                .replace(placeholderRegex, '')
                                .split('\n')
                                .map((s, i) =>
                                  i === 0 ? s : `${whitespace}${s}`,
                                )
                                .join('\n'),
                              range: monacoEditor.Range.fromPositions(
                                position,
                                position,
                              ),
                            },
                          ],
                          (undoEdits) => {
                            if (!isMinLengthArray(1, undoEdits)) {
                              return null;
                            }
                            const selection =
                              monacoEditor.Selection.fromPositions(
                                undoEdits[0].range.getStartPosition(),
                                undoEdits[0].range.getEndPosition(),
                              );
                            try {
                              scriptEditor.revealRangeInCenterIfOutsideViewport(
                                selection,
                              );
                            } catch {
                              // TODO (jason) come back to this. Calling reveal* on a line outside
                              // of the previous bounds after an insertion may crash if it tries to
                              // check the line visibility before the line gets updated. I tried a
                              // few things here to force monaco to process the model update but
                              // couldn't get anything to work. This is fine in most cases to
                              // ignore, the one possibly annoying thing would be if someone added a
                              // large insertion for which the final field was out of scroll bounds
                              // it would not get centered. This seems unlikely to happen often
                            }
                            const nextModel = scriptEditor.getModel();
                            if (cursorIndex >= 0 && nextModel) {
                              const cursorPosition = nextModel.getPositionAt(
                                nextModel.getOffsetAt(
                                  selection.getStartPosition(),
                                ) + cursorIndex,
                              );

                              return [
                                monacoEditor.Selection.fromPositions(
                                  cursorPosition,
                                  cursorPosition,
                                ),
                              ];
                            } else {
                              return [selection];
                            }
                          },
                        );
                        scriptEditor.focus();
                      }}
                      leftIcon={<IconAdd />}
                    >
                      <span className="truncate">{snippet.label}</span>
                      {'info' in snippet && (
                        <Tooltip className="ml-2" label={snippet.info}>
                          <IconInfo />
                        </Tooltip>
                      )}
                    </Button>
                  </div>
                  <div
                    className="overflow-hidden break-all rounded bg-silver-light px-4 py-2 font-mono text-xs leading-5"
                    css={css`
                      white-space: break-spaces;
                    `}
                  >
                    {snippet.snippet
                      .replace(cursorStart, '')
                      .replace(cursorEnd, '')
                      .replace(new RegExp(placeholderStart, 'g'), '')
                      .replace(new RegExp(placeholderEnd, 'g'), '')}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
